import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState, AppDispatch } from 'redux/store';
import { DocStatus } from 'data/constants/DocStatus';
import { loadDocs } from 'redux/application/documents/documents-middleware';
import { createNewDocProperties } from 'redux/application/document/document.middleware';
import BaseInput from 'components/common/BaseInput';
import {
    Actions,
    FieldGroup,
    Form,
    PrimaryButton,
    SecondaryButton,
    Section,
} from 'components/common/Modal';

const MAX_LENGTH_TITLE = 1000;
const MAX_LENGTH_DESCRIPTION = 2000;

interface AddCustomDocumentFormProps {
    closeMenu: () => void;
    mobile?: boolean;
}

const AddCustomDocumentForm = ({ closeMenu, mobile = false }: AddCustomDocumentFormProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();

    const [newDocName, setNewDocName] = useState('');
    const [newDocExplanation, setNewDocExplanation] = useState('');
    const [textareaHeight, setTextareaHeight] = useState('auto');

    const isCreateBtnEnable = newDocName.trim().length > 0;

    const createNewDoc = async () => {
        if (!activeApp) return;

        const payload = {
            appId: activeApp.id.toString(),
            payload: {
                document_name: newDocName,
                status: DocStatus.MISSING,
                explanation: newDocExplanation || null,
            },
        };

        await dispatch(createNewDocProperties(payload));
        setNewDocName('');
        setNewDocExplanation('');
        dispatch(loadDocs(activeApp.id.toString()));
        closeMenu();
    };

    const maxRows = mobile ? 6 : 15;

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const initialPadding = 40;
        setTextareaHeight(`${event.target.scrollHeight + initialPadding}px`);
        if (event.target.value.length <= MAX_LENGTH_DESCRIPTION) {
            setNewDocExplanation(event.target.value);
        }
    };

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value.length <= MAX_LENGTH_TITLE) {
            setNewDocName(event.target.value);
        }
    };

    const remainingTitleChars = MAX_LENGTH_TITLE - newDocName.length;

    return (
        <Form
            style={{ marginTop: '16px' }}
            onSubmit={(event) => {
                event.preventDefault();
                if (isCreateBtnEnable) createNewDoc();
            }}
        >
            <Section>
                <FieldGroup>
                    <BaseInput
                        fullWidth
                        size="small"
                        value={newDocName}
                        onChange={handleTitleChange}
                        label={`*${t('documentName', {
                            lng: language,
                        })} ${remainingTitleChars}`}
                        helperText={`${newDocName.length} / ${MAX_LENGTH_TITLE}`}
                    />
                </FieldGroup>
                <FieldGroup>
                    <BaseInput
                        fullWidth
                        multiline
                        size="medium"
                        value={newDocExplanation}
                        onChange={handleDescriptionChange}
                        label={t('documentExplanation', { lng: language })}
                        helperText={`${newDocExplanation.length} / ${MAX_LENGTH_DESCRIPTION}`}
                        slotProps={{
                            input: {
                                style: { height: textareaHeight, maxHeight: '320px' },
                            },
                        }}
                        maxRows={maxRows}
                    />
                </FieldGroup>
            </Section>
            <Actions>
                <SecondaryButton type="button" onClick={closeMenu}>
                    {t('close', { lng: language })}
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={!isCreateBtnEnable}>
                    {t('save', { lng: language })}
                </PrimaryButton>
            </Actions>
        </Form>
    );
};

export default AddCustomDocumentForm;
